import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { ChangeEvent } from 'preact/compat';

import { Theme } from '@app/theme';
import { SquareButton } from '@app/core';
import { Input } from '@app/form';
import {
  DashboardListContentPage,
  MainDashboardPage,
  PageTitle,
  SideMenuPage,
} from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  fullpage: {
    height: '100vh',
  },
  content: {
    width: '100%',
    margin: 'auto',
    padding: 0,
  },
  navTitle: {
    marginTop: theme.marginBase * 2,
  },
  searchLine: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
  },
  searchInput: {
    width: 320,
  },
}));

interface Props {
  navTitle: string;
  sidebar?: ReactNode;
  titleSideElement?: ReactNode;
  actions?: ReactNode;
  fetching?: boolean;
  children: ReactNode;
}

export const QuoteLayout = ({
  navTitle,
  sidebar,
  titleSideElement,
  actions,
  fetching,
  children,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  return (
    <MainDashboardPage>
      <div className={classes.fullpage}>
        <SideMenuPage
          navTitle={navTitle}
          navTitleClassName={classes.navTitle}
          hideNavOnTablet
          sideBarContent={
            <>
              <div className={classes.searchLine}>
                <Input
                  label={t('quote.load')}
                  noMargin
                  containerClass={classes.searchInput}
                  placeholder={t('quote.refOrId')}
                  value={search}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setSearch((event.currentTarget || event.target).value)
                  }
                />
                <SquareButton
                  onClick={() => navigate(`/quote/${search.trim()}`)}
                  icon="ri-search-line"
                  color="blue"
                  variant="plain"
                  disabled={!search.trim()}
                  large
                />
              </div>
              {sidebar}
            </>
          }
        >
          <div className={classes.content}>
            <PageTitle title={t('quote.title')} />
            <DashboardListContentPage
              title={t('quote.title')}
              titleSideElement={titleSideElement}
              fetching={fetching}
              actions={actions}
            >
              {children}
            </DashboardListContentPage>
          </div>
        </SideMenuPage>
      </div>
    </MainDashboardPage>
  );
};
